import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { deviceFingerprint as computeHardwareFp } from '../lib/device-fp.js';

// A stable, UNIQUE per-browser device id sent to the backend as the `X-Device-Fingerprint`
// header. The backend uses it to scope abuse blocks to a single real device instead of a
// coarse User-Agent hash that thousands of users (behind BD carrier-grade NAT + the same
// browser) share — which used to get innocent users auto-blocked.
//
// The id combines two parts:
//   1. A random UUID persisted in localStorage — GUARANTEES two browsers never collide,
//      so a legit user is never caught by someone else's block. (Cleared → new id, which
//      is fine: at worst an abuser must be re-blocked; no innocent is ever harmed.)
//   2. FingerprintJS visitorId — adds resistance to a user simply clearing storage.

const STORAGE_KEY = 'sk_device_id';

function getOrCreateUuid() {
    try {
        let id = localStorage.getItem(STORAGE_KEY);
        if (!id) {
            id = (typeof crypto !== 'undefined' && crypto.randomUUID)
                ? crypto.randomUUID()
                : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
                    const r = (Math.random() * 16) | 0;
                    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
                });
            localStorage.setItem(STORAGE_KEY, id);
        }
        return id;
    } catch {
        // localStorage blocked (private mode / disabled) — fall back to a per-session id.
        // Not persisted, but still unique enough to avoid cross-user collisions this session.
        return 'nostore-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
    }
}

// Compute once and reuse for the lifetime of the tab. Kicked off eagerly at import so the
// value is usually ready before the first auth request.
let fingerprintPromise = null;

export function getDeviceFingerprint() {
    if (!fingerprintPromise) {
        fingerprintPromise = (async () => {
            const uuid = getOrCreateUuid();
            let visitorId = '';
            try {
                const fp = await FingerprintJS.load();
                visitorId = (await fp.get()).visitorId;
            } catch {
                // FingerprintJS failed to load (blocked/offline) — the UUID alone is still
                // unique, so the block system keeps working correctly.
            }
            return `${uuid}.${visitorId}`;
        })();
    }
    return fingerprintPromise;
}

// ---------------------------------------------------------------------------
// Hardware fingerprint payload — sent as the `X-Device-Fp` header.
//
// This is the HARDWARE/OS fingerprint (installed fonts, screen, cores, memory…)
// the backend uses for FUZZY matching: it re-identifies a device across an IP
// change, a browser change, AND a screen/window resize (see lib/device-fp.js +
// lib/fp-hash.js). The backend re-derives the ids from `signals` and ignores any
// client-sent ids, so we only need to ship { signals, confidence }.
//
// The uuid header above is STILL sent (see the interceptor): the backend prefers
// this hardware payload but falls back to the uuid when confidence is low
// (Brave/Tor, too few fonts, missing APIs), keeping precise per-device scoping.
// ---------------------------------------------------------------------------

let hardwareFpPromise = null;

// URL-safe-ish base64 of a UTF-8 JSON string (handles non-ASCII font names).
function toBase64(str) {
    try {
        return btoa(unescape(encodeURIComponent(str)));
    } catch {
        return '';
    }
}

/**
 * Returns the base64-JSON payload for the `X-Device-Fp` header, or '' if the
 * fingerprint can't be computed (SSR / no canvas). Memoised for the tab.
 */
export function getDeviceFpHeader() {
    if (!hardwareFpPromise) {
        hardwareFpPromise = (async () => {
            try {
                const fp = await computeHardwareFp();
                if (!fp) return '';
                // Ship only what the server needs; it re-derives ids from signals.
                return toBase64(JSON.stringify({ signals: fp.signals, confidence: fp.confidence }));
            } catch {
                return '';
            }
        })();
    }
    return hardwareFpPromise;
}

// Warm both caches immediately so the first login already has them ready.
getDeviceFingerprint();
getDeviceFpHeader();

export default getDeviceFingerprint;
